import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete, LockReset } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { UserFormDialog } from './UserFormDialog';
import { usersApi, type User, type CreateUserInput } from '../../api/users.api';
import { extractApiError } from '../../api/error';

export function UserListPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, limit, search],
    queryFn: () => usersApi.list({ page, limit, search: search || undefined }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });

  const createMut = useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(input),
    onSuccess: (u) => { setFormOpen(false); invalidate(); setToast(`User created. Temp password: ${u.temporaryPassword}`); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    // Email is immutable and status is changed via a dedicated endpoint, so neither is part of the
    // profile update payload (the update schema rejects them). Status is applied separately if changed.
    mutationFn: async ({ id, input }: { id: number; input: CreateUserInput }) => {
      const { email: _email, status, password: _pw, ...rest } = input;
      void _email; void _pw;
      if (!rest.mobile) delete rest.mobile; // omit empty mobile (fails the 10-digit rule)
      await usersApi.update(id, rest);
      if (status && editing && status !== editing.status) await usersApi.setStatus(id, status);
    },
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => usersApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });
  const resetMut = useMutation({
    mutationFn: (id: number) => usersApi.resetPassword(id),
    onSuccess: (r) => setToast(`New temp password: ${r.temporaryPassword}`),
  });

  const columns: Column<User>[] = [
    { key: 'fullName', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : r.status === 'locked' ? 'error' : 'default'} /> },
    { key: 'roleIds', label: 'Roles', render: (r) => r.roleIds.length },
  ];

  const onSubmit = async (input: CreateUserInput) => {
    setFormError(null);
    if (editing) await updateMut.mutateAsync({ id: editing.id, input });
    else await createMut.mutateAsync(input);
  };

  return (
    <>
      <PageHeader
        title="User Management"
        action={<Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New User</Button>}
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search name / email" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Reset password"><IconButton size="small" onClick={() => resetMut.mutate(r.id)}><LockReset fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
          </>
        )}
      />

      <UserFormDialog open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSubmit={onSubmit} error={formError} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete user"
        message={`Soft-delete ${deleting?.fullName}? This can be restored by an administrator.`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={8000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
